import { useMutation } from "@apollo/client";
import { FileText, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CREATE_GBP_POST_MUTATION } from "@/graphql/google-business";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";

const MAX_CHARS = 1500;

const CTA_TYPES = [
  {
    value: "BOOK",
    labelKey: "bookLabel",
    descKey: "bookDesc",
  },
  {
    value: "LEARN_MORE",
    labelKey: "learnMoreLabel",
    descKey: "learnMoreDesc",
  },
  {
    value: "CALL",
    labelKey: "callLabel",
    descKey: "callDesc",
  },
  {
    value: "SHOP",
    labelKey: "shopLabel",
    descKey: "shopDesc",
  },
] as const;

type CtaType = (typeof CTA_TYPES)[number]["value"];

interface GbpPostUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GbpPostUpdateDialog({ open, onOpenChange }: GbpPostUpdateDialogProps) {
  const { t } = useTranslation("reviews");
  const { currentSalon } = useSalonStore();
  const [summary, setSummary] = useState("");
  const [ctaType, setCtaType] = useState<CtaType | "">("");
  const [ctaUrl, setCtaUrl] = useState("");

  const [createPost, { loading }] = useMutation(CREATE_GBP_POST_MUTATION);

  const charsLeft = MAX_CHARS - summary.length;
  const ctaValid = !ctaType || (ctaType && ctaUrl.startsWith("http"));

  const handleSubmit = async () => {
    if (!currentSalon || !summary.trim()) return;
    try {
      await createPost({
        variables: {
          salonId: currentSalon.id,
          input: {
            summary: summary.trim(),
            callToActionType: ctaType || null,
            callToActionUrl: ctaType && ctaUrl ? ctaUrl : null,
          },
        },
      });
      toast.success(t("gbpPostUpdate.toastSuccess"));
      handleClose();
    } catch {
      toast.error(t("gbpPostUpdate.toastError"));
    }
  };

  const handleClose = () => {
    setSummary("");
    setCtaType("");
    setCtaUrl("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {t("gbpPostUpdate.dialogTitle")}
          </DialogTitle>
          <DialogDescription>{t("gbpPostUpdate.dialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 py-1">
          {/* Post text */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>{t("gbpPostUpdate.postLabel")}</Label>
              <span
                className={cn(
                  "text-xs tabular-nums",
                  charsLeft < 100 ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {t("gbpPostUpdate.charsLeft", { count: charsLeft })}
              </span>
            </div>
            <Textarea
              placeholder={t("gbpPostUpdate.postPlaceholder")}
              value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, MAX_CHARS))}
              rows={10}
              className="resize-none text-sm leading-relaxed"
            />
          </div>

          {/* Optional CTA */}
          <div className="space-y-2">
            <div>
              <Label>
                {t("gbpPostUpdate.addButtonLabel")}{" "}
                <span className="text-muted-foreground font-normal text-xs">
                  ({t("gbpPostUpdate.optional")})
                </span>
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("gbpPostUpdate.ctaDescription")}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {CTA_TYPES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCtaType(ctaType === c.value ? "" : (c.value as CtaType))}
                  className={cn(
                    "text-left rounded-lg border px-3 py-2.5 transition-colors",
                    ctaType === c.value
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "border-border hover:border-foreground/30 hover:bg-muted/40"
                  )}
                >
                  <p className="text-sm font-medium leading-none">
                    {t(`gbpPostUpdate.ctaTypes.${c.labelKey}`)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t(`gbpPostUpdate.ctaTypes.${c.descKey}`)}
                  </p>
                </button>
              ))}
            </div>
            {ctaType && (
              <div className="space-y-1">
                <Input
                  placeholder={t("gbpPostUpdate.urlPlaceholder")}
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                />
                {ctaUrl && !ctaUrl.startsWith("http") && (
                  <p className="text-xs text-destructive">{t("gbpPostUpdate.urlError")}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {t("gbpPostUpdate.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!summary.trim() || !ctaValid || loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("gbpPostUpdate.publishPost")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
